import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { formatTooltipValue } from '../utils/formatters';
import { einzelplanName, lookupLabel, t, useT } from '../i18n';
import type { TitleYearMeta } from '../types';

// Two muted lines under the title: EP on top, KP below. Each line is null when
// the corresponding piece is missing, so empty rows collapse cleanly.
function formatTitleMeta(meta: TitleYearMeta | undefined): string[] {
  if (!meta) return [];
  const lines: string[] = [];
  const m = t();
  if (meta.einzelplan) {
    const value = meta.einzelplanText ? `${meta.einzelplan} ${einzelplanName(meta.einzelplanText)}` : meta.einzelplan;
    lines.push(`${m.tooltip.einzelplanShort}: ${value}`);
  }
  if (meta.kapitel) {
    const value = meta.kapitelText ? `${meta.kapitel} ${lookupLabel('kapitel', meta.kapitelText)}` : meta.kapitel;
    lines.push(`${m.tooltip.kapitelShort}: ${value}`);
  }
  return lines;
}

// Tooltip chrome — publishing chart engine: white background, 1px solid
// black border, 8px padding, square corners, no shadow (.tooltip in
// createChart.js).
const TooltipContainer = styled.div`
  background: #FFFFFF;
  border: 1px solid #000000;
  padding: 8px;
  max-width: 350px;
`;

const TooltipHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xs};
`;

// Round 10px dot — same shape language as the legend chips (§5: prefer
// circle/rectangle; the engine's series dot is a 10px circle).
const ColorDot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.color};
  flex-shrink: 0;
`;

const TooltipTitle = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;

const TooltipSubtitle = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.xs};
  display: block;
`;

const TitleMetaLine = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.muted};
  margin-bottom: ${theme.spacing.xs};
  display: block;
  line-height: 1.3;
`;

const TooltipValue = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.lg};
  font-weight: ${theme.fontWeight.bold};
  color: ${theme.colors.text.primary};
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xs};
`;

const TooltipRowLabel = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.secondary};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TooltipRowValue = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.xs};
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  flex-shrink: 0;
`;

const TooltipMore = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.muted};
  margin-top: ${theme.spacing.xs};
  display: block;
`;

const NoDataText = styled.span`
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.muted};
  font-style: italic;
`;

interface SingleValueTooltipProps {
  name: string;
  value: number;
  color: string;
  year: number;
  isIst: boolean;
  valueFormatter?: (value: number) => string;
  meta?: TitleYearMeta;
}

export function SingleValueTooltip({ name, value, color, year, isIst, valueFormatter = formatTooltipValue, meta }: SingleValueTooltipProps) {
  const m = useT();
  const metaLines = formatTitleMeta(meta);
  return (
    <TooltipContainer>
      <TooltipHeader>
        <ColorDot color={color} />
        <TooltipTitle>{name}</TooltipTitle>
      </TooltipHeader>
      {metaLines.map((line, i) => (
        <TitleMetaLine key={i}>{line}</TitleMetaLine>
      ))}
      <TooltipSubtitle>{m.tooltip.yearWithType(year, isIst)}</TooltipSubtitle>
      <TooltipValue>{valueFormatter(value)}</TooltipValue>
    </TooltipContainer>
  );
}

interface MultiValueTooltipEntry {
  name: string;
  value: number;
  color: string;
  dataKey?: string;
  meta?: TitleYearMeta;
}

interface MultiValueTooltipProps {
  year: number;
  isIst: boolean;
  entries: MultiValueTooltipEntry[];
  hoveredEntry?: string | null;
  maxEntries?: number;
  valueFormatter?: (value: number) => string;
}

export function MultiValueTooltip({
  year,
  isIst,
  entries,
  hoveredEntry,
  maxEntries = 6,
  valueFormatter = formatTooltipValue,
}: MultiValueTooltipProps) {
  const m = useT();
  // If only one entry, show SingleValueTooltip directly
  if (entries.length === 1) {
    const entry = entries[0];
    return (
      <SingleValueTooltip
        name={entry.name}
        value={entry.value}
        color={entry.color}
        year={year}
        isIst={isIst}
        valueFormatter={valueFormatter}
        meta={entry.meta}
      />
    );
  }

  // If hovering a specific entry, show only that one
  // Match on dataKey (id) if available, otherwise match on name
  if (hoveredEntry) {
    const entry = entries.find(e => (e.dataKey || e.name) === hoveredEntry);
    if (entry) {
      return (
        <SingleValueTooltip
          name={entry.name}
          value={entry.value}
          color={entry.color}
          year={year}
          isIst={isIst}
          valueFormatter={valueFormatter}
          meta={entry.meta}
        />
      );
    }
  }

  const sortedEntries = [...entries].sort((a, b) => b.value - a.value);
  const total = sortedEntries.reduce((sum, e) => sum + e.value, 0);
  const displayedEntries = sortedEntries.slice(0, maxEntries);
  const remainingCount = sortedEntries.length - maxEntries;

  return (
    <TooltipContainer>
      <TooltipSubtitle>{m.tooltip.yearWithType(year, isIst)}</TooltipSubtitle>
      <TooltipValue style={{ marginBottom: theme.spacing.sm, display: 'block' }}>
        {m.tooltip.total(valueFormatter(total))}
      </TooltipValue>
      {displayedEntries.map(entry => (
        <TooltipRow key={entry.name}>
          <ColorDot color={entry.color} style={{ width: 10, height: 10 }} />
          <TooltipRowLabel>{entry.name}:</TooltipRowLabel>
          <TooltipRowValue>{valueFormatter(entry.value)}</TooltipRowValue>
        </TooltipRow>
      ))}
      {remainingCount > 0 && (
        <TooltipMore>{m.tooltip.more(remainingCount)}</TooltipMore>
      )}
    </TooltipContainer>
  );
}

// Color constants matching ExpenditureAreaChart (DZ catalog: black + red accent)
const IST_LINE_COLOR = '#000000';
const SOLL_LINE_COLOR = '#DA454E';

interface IstSollTooltipProps {
  year: number;
  name: string;
  sollValue: number;
  istValue: number | null;
  valueFormatter?: (value: number) => string;
  meta?: TitleYearMeta;
}

export function IstSollTooltip({ year, name, sollValue, istValue, valueFormatter = formatTooltipValue, meta }: IstSollTooltipProps) {
  const m = useT();
  const hasIst = istValue !== null && istValue !== undefined;
  const metaLines = formatTitleMeta(meta);

  return (
    <TooltipContainer>
      <TooltipHeader>
        <TooltipTitle>{name}</TooltipTitle>
      </TooltipHeader>
      {metaLines.map((line, i) => (
        <TitleMetaLine key={i}>{line}</TitleMetaLine>
      ))}
      <TooltipSubtitle>{m.common.year(year)}</TooltipSubtitle>

      {/* Soll row */}
      <TooltipRow>
        <ColorDot color={SOLL_LINE_COLOR} style={{ width: 10, height: 10 }} />
        <TooltipRowLabel>{m.tooltip.sollLabel}</TooltipRowLabel>
        <TooltipRowValue>{valueFormatter(sollValue)}</TooltipRowValue>
      </TooltipRow>

      {/* Ist row */}
      <TooltipRow>
        <ColorDot color={IST_LINE_COLOR} style={{ width: 10, height: 10 }} />
        <TooltipRowLabel>{m.tooltip.istLabel}</TooltipRowLabel>
        {hasIst ? (
          <TooltipRowValue>{valueFormatter(istValue)}</TooltipRowValue>
        ) : (
          <NoDataText>{m.common.noData}</NoDataText>
        )}
      </TooltipRow>
    </TooltipContainer>
  );
}
